import { useCallback, useEffect, useRef } from 'react';

import { useAdjustEvents } from '@order/integrations-adjust';
import { useAppsFlyer } from '@order/integrations-appsflyer';
import { useTelemetry } from '@order/Telemetry';

import { usePostLoginRedirectHref } from '../../context/AuthMachine';
import { useCustomer } from '../../context/Customer';

// ─────────────────────────────────────────────────────────────────────────────

export const useTrackSignInSuccess = () => {
  const postLoginRedirectHref = usePostLoginRedirectHref();
  const { customer } = useCustomer();
  const { track } = useTelemetry();
  const { logLoginEvent } = useAppsFlyer();
  const { trackAdjustLogin } = useAdjustEvents();

  // ─── Refs ────────────────────────────────────────────────────────────

  const trackRef = useRef(track);
  const logLoginEventRef = useRef(logLoginEvent);
  const trackAdjustLoginRef = useRef(trackAdjustLogin);
  const hasPendingSignInRef = useRef(false);

  trackRef.current = track;
  logLoginEventRef.current = logLoginEvent;
  trackAdjustLoginRef.current = trackAdjustLogin;

  // ─── Handlers ────────────────────────────────────────────────────────

  /**
   * NOTE: The post-login redirect target is cleared once the redirect runs,
   *       which can be before the customer loads, so it only marks a pending sign-in.
   */
  const markSignInAsPending = useCallback(() => {
    if (!postLoginRedirectHref) {
      return;
    }

    hasPendingSignInRef.current = true;
  }, [postLoginRedirectHref]);

  const trackPendingSignIn = useCallback(() => {
    if (!hasPendingSignInRef.current || !customer.id) {
      return;
    }

    hasPendingSignInRef.current = false;

    trackRef.current('sign-in.success', { userId: customer.id });
    logLoginEventRef.current();
    trackAdjustLoginRef.current();
  }, [customer.id]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(markSignInAsPending, [markSignInAsPending]);
  useEffect(trackPendingSignIn, [postLoginRedirectHref, trackPendingSignIn]);
};
